import { loadJwt } from "../dwclient/localstorage/jwtStorage";
import bs58 from "bs58";
import { registerActiveUpload, unregisterActiveUpload } from "./uploadworkerutil";

/*type UploadFile = {
  filesUrl: string;
  file: File;
  authToken: string | null;
  message: string;
  path: string;
  signature: string | null;
  fileName: string;
  fileSize: number;
  fileType: string;
  objectURL: string;
};*/

const MAX_CHUNK_SIZE = process.env.MAX_CHUNK_SIZE ? parseInt(process.env.MAX_CHUNK_SIZE) : 67_108_836;
const MAX_CHUNK_SIZE_GROSS = MAX_CHUNK_SIZE + 12 + 16;

let currentSyncXhr = null;
let currentSyncAbortController = null;
let currentSyncUploadInfo = null;
let isSyncInterrupted = false;

export async function abortSyncUpload() {
  console.log("Aborting sync upload");
  isSyncInterrupted = true;
  
  if (currentSyncXhr) {
    try {
      console.log("Aborting current sync XHR request");
      currentSyncXhr.abort();
    } catch (error) {
      console.warn("Error aborting sync XHR:", error);
    }
    currentSyncXhr = null;
  }
  
  if (currentSyncAbortController) {
    try {
      console.log("Aborting current sync fetch request");
      currentSyncAbortController.abort();
    } catch (error) {
      console.warn("Error aborting sync fetch:", error);
    }
    currentSyncAbortController = null;
  }
  
  if (currentSyncUploadInfo) {
    try {
      const { filesUrl, authToken, path, uploadId } = currentSyncUploadInfo;
      console.log("Aborting sync upload on server:", uploadId);
      await abortUploadOnServer(filesUrl, authToken, path, uploadId);
    } catch (error) {
      console.warn("Error aborting sync upload on server:", error);
    }
    currentSyncUploadInfo = null;
  }
}

export function resetSyncUploadState() {
  currentSyncXhr = null;
  currentSyncAbortController = null;
  currentSyncUploadInfo = null;
}

export async function syncUploadFile(
  filesUrl,
  file,
  message,
  path,
  signature,
  onUploadProgress,
  onCompleted,
  onFinished,
) {
  if (isSyncInterrupted) {
    isSyncInterrupted = false;
  }
  resetSyncUploadState();
  
  const authToken = await loadJwt();
  const objectURL = URL.createObjectURL(file);
  const entity = {
    filesUrl,
    authToken,
    file,
    message,
    path,
    signature,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    objectURL,
  };
  
  try {
    if (isSyncInterrupted) {
      throw new Error("Upload aborted");
    }
    
    if (file.size > MAX_CHUNK_SIZE) {
      console.log("sync large file");
      await uploadLargeFileWithProgress(entity, onUploadProgress);
      
      if (isSyncInterrupted) {
        throw new Error("Upload aborted");
      }
      
      onCompleted({
        filePath: entity.path,
        file: { name: entity.fileName, size: entity.fileSize, type: entity.fileType },
        status: "completed",
        ...(await getFilesStatistics()),
      });
      onFinished();
    } else {
      console.log("sync small file");
      await uploadSmallFileWithProgress(entity, onUploadProgress);
      
      if (isSyncInterrupted) {
        throw new Error("Upload aborted");
      }
      
      onCompleted({
        filePath: entity.path,
        file: { name: entity.fileName, size: entity.fileSize, type: entity.fileType },
        status: "completed",
        ...(await getFilesStatistics()),
      });
      onFinished();
    }
  } catch (error) {
    if (isSyncInterrupted || error.message === "Upload aborted") {
      console.log("Sync upload aborted by user");
      onCompleted({
        filePath: entity.path,
        file: { name: entity.fileName, size: entity.fileSize, type: entity.fileType },
        status: "interrupted",
        message: "Upload cancelled by user",
        ...(await getFilesStatistics()),
      });
      onFinished();
    } else {
      console.error("Upload failed:", error);
      onCompleted({
        filePath: entity.path,
        file: { name: entity.fileName, size: entity.fileSize, type: entity.fileType },
        status: "error",
        message: error.message || "Upload failed",
        ...(await getFilesStatistics()),
      });
      onFinished();
    }
  } finally {
    if (objectURL) {
      URL.revokeObjectURL(objectURL);
    }
    currentSyncXhr = null;
  }
}

async function uploadLargeFileWithProgress(entity /*: UploadFile*/, onUploadProgress) /*: Promise<void>*/ {
  const uploadId = await beginUpload(entity);
  console.log(uploadId);
  
  currentSyncUploadInfo = {
    filesUrl: entity.filesUrl,
    authToken: entity.authToken,
    path: entity.path,
    uploadId: uploadId,
  };
  
  registerActiveUpload(entity.path, uploadId, entity.filesUrl);
  try {
    await uploadFileInChunks(entity, uploadId, onUploadProgress);
    
    if (isSyncInterrupted) {
      throw new Error("Upload aborted");
    }
    
    await commitUpload(entity, uploadId);
    currentSyncUploadInfo = null;
  } finally {
    unregisterActiveUpload(entity.path);
  }
}

export async function abortUploadOnServer(filesUrl, authToken, path, uploadId) {
  const message = encodeSendObjectToBase58({ path, uploadId });
  const params = new URLSearchParams();
  params.append("message", message);
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    
    const response = await fetch(`${filesUrl}/abort-upload?${params}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.warn(`Abort upload failed with status ${response.status} for ${path}`);
      return false;
    }
    
    console.log(`Successfully aborted upload: ${path}`);
    return true;
  } catch (error) {
    console.warn(`Failed to abort upload for ${path}:`, error.message);
    return false;
  }
}

const beginUpload = async (entity /*: UploadFile*/, retryAttempt = 0) => {
  if (isSyncInterrupted) {
    throw new Error("Upload aborted");
  }
  
  const params = new URLSearchParams();
  params.append("message", entity.message);
  
  const controller = new AbortController();
  currentSyncAbortController = controller;
  const timeoutId = setTimeout(() => controller.abort(), 60000);
  
  try {
    const response = await fetch(`${entity.filesUrl}/begin-upload?${params}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${entity.authToken}`,
        "Content-Type": entity.fileType,
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    currentSyncAbortController = null;
    
    if (!response.ok) {
      throw new Error(`Begin upload failed with status ${response.status}`);
    }
    
    const responseBody = await response.json();
    return responseBody.uploadId;
  } catch (error) {
    clearTimeout(timeoutId);
    currentSyncAbortController = null;
    
    if (error.name === "AbortError" && isSyncInterrupted) {
      throw new Error("Upload aborted");
    }
    
    if (retryAttempt < MAX_RETRY_ATTEMPTS && !isSyncInterrupted) {
      console.warn(`Begin upload failed, retrying (attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS})...`, error.message);
      await new Promise(resolve => setTimeout(resolve, 2000 * (retryAttempt + 1)));
      return beginUpload(entity, retryAttempt + 1);
    }
    throw new Error(`Begin upload failed after ${MAX_RETRY_ATTEMPTS} retries: ${error.message}`);
  }
};

const uploadFileInChunks = async (entity /*: UploadFile*/, uploadId /*: string*/, onUploadProgress) => {
  const symmetricKey = await createFromSignature(entity.signature);
  const startTimestamp = Date.now();
  const totalChunks = Math.ceil(entity.fileSize / MAX_CHUNK_SIZE);
  const encryptedChunk64m = new Uint8Array(MAX_CHUNK_SIZE_GROSS);
  await streamFileInChunks(entity.objectURL, MAX_CHUNK_SIZE, async (originalArray64m, i) => {
    const start = i * MAX_CHUNK_SIZE;
    const end = Math.min(start + MAX_CHUNK_SIZE, entity.fileSize);
    const chunkSize = end - start;
    await encryptData(symmetricKey, originalArray64m.subarray(0, chunkSize), encryptedChunk64m);

    await uploadChunk(
      entity,
      encryptedChunk64m.subarray(0, end - start + 12 + 16),
      i,
      totalChunks,
      uploadId,
      start,
      entity.fileSize,
      onUploadProgress,
      startTimestamp,
    );
    if (globalThis.gc) globalThis.gc();
  });

  //const originalArray64m = new Uint8Array(MAX_CHUNK_SIZE);
  /*  for (let i = 0; i < totalChunks; i++) {
    const start = i * MAX_CHUNK_SIZE;
    const end = Math.min(start + MAX_CHUNK_SIZE, entity.fileSize);
    let chunk = file.slice(start, end);
    const chunkSize = end - start;
    await blobToUintArray(chunk, originalArray64m);
    chunk = null;
    console.log("chunkSize", chunkSize);
    await encryptData(symmetricKey, originalArray64m.subarray(0, chunkSize), encryptedChunk64m);

    await uploadChunk(
      entity,
      encryptedChunk64m.subarray(0, end - start + 12 + 16),
      i,
      totalChunks,
      uploadId,
      start,
      entity.fileSize,
    );*/

  /*getFilesStatistics().then((stat) => {
      const estimated = calculateEstimated(startTimestamp, stat.loadedFilesBytes + end, stat.totalFilesBytes);
      stat.loadedFilesBytes = stat.loadedFilesBytes + end;
      notifyClients({
        file: entity,
        fileId: entity.id,
        status: "uploading",
        loaded: end,
        total: file.size,
        estimated,
        ...stat,
      });
    });*/
  // Explicitly clear buffers
  //encryptedChunk.fill(0);
  //encryptedChunk = null;
  /*    if (globalThis.gc) globalThis.gc();
  }*/

  console.log("Upload complete:", entity.fileName);
};

async function streamFileInChunks(url, chunkSize, processChunk) {
  const response = await fetch(url);
  const reader = response.body?.getReader();
  let buffer = new Uint8Array(chunkSize); // Fixed-size buffer
  let offset = 0; // Tracks how much buffer is filled
  let i = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break; // Exit loop when stream ends

    let start = 0;
    while (start < value.length) {
      const remaining = chunkSize - offset; // Space left in buffer
      const lengthToCopy = Math.min(remaining, value.length - start);

      // Copy only the needed part of `value` into `buffer`
      buffer.set(value.subarray(start, start + lengthToCopy), offset);
      offset += lengthToCopy;
      start += lengthToCopy;

      // If buffer is full, process it and start a new one
      if (offset === chunkSize) {
        await processChunk(buffer, i); // Process the full chunk
        i++;
        //buffer = new Uint8Array(chunkSize); // Allocate a new buffer
        buffer.fill(0);
        offset = 0; // Reset offset
      }
    }
  }

  // Process last remaining bytes (if any) without discarding them
  if (offset > 0) {
    await processChunk(buffer.subarray(0, offset), i); // Process only filled portion
  }
}

const createFromSignature = async (signature) /*: Promise<CryptoKey>*/ => {
  // 4. Hash the signature to derive key material
  const hashBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(signature));
  const keyMaterial = new Uint8Array(hashBuffer);

  // 5. Derive a symmetric key
  const symmetricKey = await crypto.subtle.importKey("raw", keyMaterial, { name: "AES-GCM" }, true, [
    "encrypt",
    "decrypt",
  ]);
  return symmetricKey;
};

/*const blobToUintArray = async (blob, uintarray) /*: Promise<Uint8Array> => {
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
};*/

const blobToUintArray = async (blob, uintarray) => {
  const reader = blob.stream().getReader();
  let offset = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    uintarray.set(value, offset);
    offset += value.length;
  }

  return uintarray;
};

const encryptData = async (symmetricKey, plaintextBytes, output) => {
  // Generate a 12-byte IV (Initialization Vector) for AES-GCM
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96 bits (12 bytes) is standard for AES-GCM
  //const iv = new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  // Encrypt the byte array (plaintextBytes)
  let encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv }, // AES-GCM with the IV
    symmetricKey,
    plaintextBytes, // Expecting Uint8Array (byte array)
  );

  // Convert the ciphertext from ArrayBuffer to Uint8Array
  let ciphertext = new Uint8Array(encrypted);
  //console.log("ciphertext length:", ciphertext.byteLength);
  //console.log("Start of cipherText", ciphertext.slice(0, 32));

  // Create a combined array: First 12 bytes are the IV, the rest is the ciphertext
  //const combined = new Uint8Array(iv.length + ciphertext.length);
  //console.log("combined length:", combined.byteLength);
  output.set(iv, 0);
  output.set(ciphertext, iv.length);
  //combined.set(iv, 0); // Place the IV at the beginning
  //combined.set(ciphertext, iv.length); // Place the ciphertext after the IV

  // Explicitly clear memory (plaintext and temporary buffers)
  //plaintextBytes.fill(0);
  //ciphertext.fill(0);
  //ciphertext = null;
  ciphertext.fill(0);
  ciphertext = null;
  encrypted = null;
  iv.fill(0);

  // Return the combined IV + ciphertext array (byte array)
  //return combined;
};

/*const uploadChunk = async (
  entity,
  encryptedChunk, //: Uint8Array<ArrayBuffer>
  chunkIndex, //: number
  totalChunks, //: number
  uploadId //:string
) => {
  const message = encodeSendObjectToBase58({ path: entity.path, uploadId, partNumber: chunkIndex });
  const params = new URLSearchParams();
  params.append("message", message);

  const response = await fetch(`${entity.filesUrl}/upload-part?${params}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${entity.authToken}`,
    },
    body: encryptedChunk,
  });
  const responseBody = await response.json();
  console.log(responseBody);
};*/

const CHUNK_UPLOAD_TIMEOUT_MS = 300000; // 5 minutes timeout per chunk
const MAX_RETRY_ATTEMPTS = 3;

const uploadChunk = (
  entity,
  encryptedChunk /*: Uint8Array<ArrayBuffer>*/,
  chunkIndex /*: number*/,
  totalChunks /*: number*/,
  uploadId /*: string*/,
  prevChunksBytes,
  size,
  onUploadProgress,
  startTimestamp,
  retryAttempt = 0,
) => {
  return new Promise((resolve, reject) => {
    if (isSyncInterrupted) {
      reject(new Error("Upload aborted"));
      return;
    }

    const message = encodeSendObjectToBase58({ path: entity.path, uploadId, partNumber: chunkIndex });
    const params = new URLSearchParams();
    params.append("message", message);

    const xhr = new XMLHttpRequest();
    currentSyncXhr = xhr;
    
    xhr.open("POST", `${entity.filesUrl}/upload-part?${params}`, true);
    xhr.setRequestHeader("Authorization", `Bearer ${entity.authToken}`);
    xhr.timeout = CHUNK_UPLOAD_TIMEOUT_MS;

    let lastProgressTime = Date.now();
    let progressCheckInterval = null;

    const startProgressCheck = () => {
      progressCheckInterval = setInterval(() => {
        const timeSinceLastProgress = Date.now() - lastProgressTime;
        if (timeSinceLastProgress > 60000) {
          console.warn(`No progress for ${Math.round(timeSinceLastProgress / 1000)}s on chunk ${chunkIndex}`);
        }
      }, 30000);
    };

    const clearProgressCheck = () => {
      if (progressCheckInterval) {
        clearInterval(progressCheckInterval);
        progressCheckInterval = null;
      }
    };

    xhr.upload.onprogress = (progress) => {
      lastProgressTime = Date.now();
      const currentLoaded = prevChunksBytes + progress.loaded;
      const estimated = calculateEstimated(startTimestamp, currentLoaded, size);
      getFilesStatistics().then((stat) => {
        onUploadProgress({
          file: { file: { name: entity.fileName, size: entity.fileSize } },
          fileId: entity.id,
          status: "uploading",
          loaded: currentLoaded,
          total: size,
          estimated: estimated,
          ...stat,
        });
      });
    };

    xhr.onload = () => {
      clearProgressCheck();
      currentSyncXhr = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const responseBody = JSON.parse(xhr.responseText);
          encryptedChunk.fill(0);
          encryptedChunk = null;
          resolve(responseBody);
        } catch (error) {
          reject(new Error("Failed to parse response"));
        }
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      clearProgressCheck();
      currentSyncXhr = null;
      if (retryAttempt < MAX_RETRY_ATTEMPTS && !isSyncInterrupted) {
        console.warn(`Network error on chunk ${chunkIndex}, retrying (attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS})...`);
        setTimeout(() => {
          uploadChunk(entity, encryptedChunk, chunkIndex, totalChunks, uploadId, prevChunksBytes, size, onUploadProgress, startTimestamp, retryAttempt + 1)
            .then(resolve)
            .catch(reject);
        }, 2000 * (retryAttempt + 1));
      } else {
        reject(new Error(`Network error occurred after ${MAX_RETRY_ATTEMPTS} retries`));
      }
    };

    xhr.ontimeout = () => {
      clearProgressCheck();
      currentSyncXhr = null;
      if (retryAttempt < MAX_RETRY_ATTEMPTS && !isSyncInterrupted) {
        console.warn(`Timeout on chunk ${chunkIndex}, retrying (attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS})...`);
        setTimeout(() => {
          uploadChunk(entity, encryptedChunk, chunkIndex, totalChunks, uploadId, prevChunksBytes, size, onUploadProgress, startTimestamp, retryAttempt + 1)
            .then(resolve)
            .catch(reject);
        }, 2000 * (retryAttempt + 1));
      } else {
        reject(new Error(`Upload timeout after ${CHUNK_UPLOAD_TIMEOUT_MS / 1000}s and ${MAX_RETRY_ATTEMPTS} retries`));
      }
    };
    
    xhr.onabort = () => {
      clearProgressCheck();
      currentSyncXhr = null;
      console.log("XHR sync chunk upload aborted");
      reject(new Error("Upload aborted"));
    };

    xhr.onloadend = () => {
      clearProgressCheck();
      currentSyncXhr = null;
      xhr.upload.onprogress = null;
      xhr.onload = null;
      xhr.onerror = null;
      xhr.ontimeout = null;
      xhr.onabort = null;
      xhr.onloadend = null;
    };

    startProgressCheck();
    xhr.send(encryptedChunk);
  });
};

function encodeSendObjectToBase58(sendObject) {
  const sendString = JSON.stringify(sendObject);
  const sendBytesEncoded = encodeToBase58(sendString);
  return sendBytesEncoded;
}

function encodeToBase58(plainText) {
  const sendBytes = new TextEncoder().encode(plainText);
  // eslint-disable-next-line no-undef
  console.log(bs58);
  const sendBytesEncoded = bs58.encode(sendBytes);
  return sendBytesEncoded;
}

const commitUpload = async (entity, uploadId /*: string*/, retryAttempt = 0) => {
  if (isSyncInterrupted) {
    throw new Error("Upload aborted");
  }
  
  const message = encodeSendObjectToBase58({ path: entity.path, uploadId });
  const params = new URLSearchParams();
  params.append("message", message);
  
  const controller = new AbortController();
  currentSyncAbortController = controller;
  const timeoutId = setTimeout(() => controller.abort(), 60000);
  
  try {
    const response = await fetch(`${entity.filesUrl}/commit-upload?${params}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${entity.authToken}`,
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    currentSyncAbortController = null;
    
    if (!response.ok) {
      throw new Error(`Commit upload failed with status ${response.status}`);
    }
    
    let responseBody = await response.json();
    console.log(responseBody);
    responseBody = null;
  } catch (error) {
    clearTimeout(timeoutId);
    currentSyncAbortController = null;
    
    if (error.name === "AbortError" && isSyncInterrupted) {
      throw new Error("Upload aborted");
    }
    
    if (retryAttempt < MAX_RETRY_ATTEMPTS && !isSyncInterrupted) {
      console.warn(`Commit upload failed, retrying (attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS})...`, error.message);
      await new Promise(resolve => setTimeout(resolve, 2000 * (retryAttempt + 1)));
      return commitUpload(entity, uploadId, retryAttempt + 1);
    }
    throw new Error(`Commit upload failed after ${MAX_RETRY_ATTEMPTS} retries: ${error.message}`);
  }
};

async function uploadSmallFileWithProgress(entity /*: UploadFile*/, onUploadProgress) /*: Promise<void>*/ {
  if (isSyncInterrupted) {
    throw new Error("Upload aborted");
  }
  
  const file = await (await fetch(entity.objectURL)).blob();
  const encryptedFile = await encryptFile(entity.signature, file);
  console.log("upload small file", entity);
  console.log("uploadWorker encrypted bytes", await encryptedFile.arrayBuffer());
  return new Promise((resolve, reject) => {
    if (isSyncInterrupted) {
      reject(new Error("Upload aborted"));
      return;
    }
    
    console.log("starting to upload file:", entity);
    const xhr = new XMLHttpRequest();
    currentSyncXhr = xhr;
    
    const formData = new FormData();
    formData.append("file", encryptedFile);
    formData.append("message", entity.message);
    console.log("formdata created", formData);
    const startTimestamp = Date.now();
    xhr.open("POST", entity.filesUrl + "/write", true);
    console.log("xhr opened");
    xhr.setRequestHeader("Authorization", "Bearer " + entity.authToken);

    xhr.upload.onprogress = (event) => {
      console.log("upload progressing", event);
      getFilesStatistics().then((stat) => {
        if (event.lengthComputable) {
          const estimated = calculateEstimated(startTimestamp, event.loaded, event.total);
          onUploadProgress({
            file: { file: { name: entity.fileName, size: entity.fileSize } },
            fileId: entity.id,
            status: "uploading",
            loaded: event.loaded,
            total: event.total,
            estimated,
            ...stat,
          });
        }
      });
    };

    xhr.onload = () => {
      currentSyncXhr = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status} and response ${xhr.responseText}`));
      }
    };

    xhr.onerror = () => {
      currentSyncXhr = null;
      console.log("upload error");
      reject(new Error("Network error"));
    };
    
    xhr.onabort = () => {
      currentSyncXhr = null;
      console.log("XHR sync small file upload aborted");
      reject(new Error("Upload aborted"));
    };
    
    xhr.onloadend = () => {
      currentSyncXhr = null;
      xhr.upload.onprogress = null;
      xhr.onload = null;
      xhr.onerror = null;
      xhr.onabort = null;
      xhr.onloadend = null;
    };
    
    xhr.send(formData);
  });
}

/*function notifyClients(message) {
  connections.forEach((connPort) => {
    try {
      connPort.postMessage(message);
    } catch (error) {
      console.log(error);
    }
  });
}*/

function notifyClients(message) {
  // TODO broadcast.postMessage(message);
}

const encryptFile = async (signature, plainFile) /*: Promise<File>*/ => {
  const fileContent = new Uint8Array(MAX_CHUNK_SIZE);
  const encodedBytes = new Uint8Array(MAX_CHUNK_SIZE_GROSS);
  await blobToUintArray(plainFile, fileContent);
  await encryptData(await createFromSignature(signature), fileContent.subarray(0, plainFile.size), encodedBytes);
  const encodedSubArray = encodedBytes.subarray(0, plainFile.size + 16 + 12);
  return new File([encodedSubArray], plainFile.name, { type: plainFile.type, lastModified: plainFile.lastModified });
};

async function getFilesStatistics() {
  let loadedFiles = 0;
  let totalFiles = 1;
  let loadedFilesBytes = 0;
  let totalFilesBytes = 0;
  return { loadedFiles, totalFiles, loadedFilesBytes, totalFilesBytes };
}

function calculateEstimated(startTimestamp, loadedBytes, totalBytes) {
  const currentTimestamp = Date.now();
  const elapsedMillis = currentTimestamp - startTimestamp;
  const rate = elapsedMillis / loadedBytes;
  return (rate * (totalBytes - loadedBytes)) / 1000.0;
}
